import React, {
  PropsWithChildren,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';

type ModalProps = {
  parentRef: React.RefObject<HTMLDivElement>;
  supportScrolling: boolean;
} & PropsWithChildren;

export interface ExpandableModalChildProps {
  isModalOpen?: boolean;
  handleModalOpen?: () => void;
  handleModalClose?: () => void;
  style?: any;
}

/**
 * @TODO
 * Things that need to be adjusted for this component to be used globally:
 * initial width and height has extra calcls
 * Height after animation has margin top calcs
 * Width after animation has margin right calcs
 */

const ExpandableModal = ({
  children,
  parentRef,
  supportScrolling,
}: ModalProps) => {
  const [clone, setClone] = useState<React.ReactElement | null>(null);
  const childRef = useRef<HTMLDivElement | null>(null);
  const initialChildStyle = useRef<HTMLElement | null>(null);

  const handleModalClose = useCallback(() => {
    const childArray = React.Children.toArray(children) as React.ReactElement[];
    const childToClone = childArray[0];

    // Create the cloned element
    const clonedElement = React.cloneElement(childToClone, {
      isModalOpen: false,
      style: { ...initialChildStyle.current, zIndex: 0 },
    });

    setClone(clonedElement);

    // // Remove after animation
    setTimeout(() => {
      setClone(null);
      initialChildStyle.current = null;
    }, 500);
  }, [children]);

  const handleModalOpen = useCallback(() => {
    const childArray = React.Children.toArray(children) as React.ReactElement[];
    const childToClone = childArray[0];

    const childElementRef = childRef.current;
    if (!childElementRef || !parentRef?.current) return;

    // Get position of the original child
    const childRect = childElementRef.getBoundingClientRect();
    const parentRect = parentRef.current.getBoundingClientRect();

    // Account for parent container scroll
    const parentScrollTop = parentRef.current.scrollTop || 0;
    const parentScrollLeft = parentRef.current.scrollLeft || 0;

    const parentOffsetTop = parentRef.current.offsetTop;

    // Calculate initial position relative to parent
    const initialTop = childRect.top - parentRect.top + parentScrollTop;
    const initialLeft = childRect.left - parentRect.left + parentScrollLeft;

    // Initial style (matches original child)
    const childStyle = childToClone.props.style || {};
    const initialStyle = {
      ...childStyle,
      position: 'absolute',
      top: initialTop,
      left: initialLeft,
      width: childRect.width - 16 - 20, // - padding - gap
      height: childRect.height - 16 - 20, // - padding - gap
      marginTop: 0,
      zIndex: 1000,
      transition: 'all 0.5s ease',
      opacity: 1,
      overflow: 'hidden',
    };

    initialChildStyle.current = initialStyle;

    // Create the cloned element
    const clonedElement = React.cloneElement(childToClone, {
      isModalOpen: true,
      handleModalClose,
      style: initialStyle,
    });

    setClone(clonedElement);

    const top = supportScrolling ? parentOffsetTop - parentRect.y : 0;
    const height = supportScrolling
      ? window.innerHeight - parentOffsetTop - 44 - 30
      : parentRect.height - 32; // - margintTop - marginHorizontal

    // Trigger animation after rendering
    setTimeout(() => {
      setClone(
        React.cloneElement(clonedElement, {
          isModalOpen: true,
          handleModalClose,
          style: {
            position: 'absolute',
            top,
            left: 0,
            zIndex: 1000,
            width: parentRect.width - 30, // - paddingRight
            height,
            marginTop: 0,
            transition: 'all 0.5s ease',
            opacity: 1,
            overflow: 'hidden',
          },
        }),
      );
    }, 0);
  }, [children, handleModalClose, parentRef, supportScrolling]);

  const modifiedChildren = React.Children.map(children, (child) => {
    // Check if child is a valid React element
    if (React.isValidElement<ExpandableModalChildProps>(child)) {
      // Use cloneElement to add the new prop
      return React.cloneElement<
        ExpandableModalChildProps & { ref?: RefObject<HTMLElement> }
      >(child, {
        ref: childRef,
        handleModalOpen,
      });
    }

    return child; // Return unchanged if not a valid element
  });

  return (
    <>
      {modifiedChildren}
      {clone}
    </>
  );
};

export default ExpandableModal;
